import './style.scss'

import React, { Component } from 'react'
import moment from 'moment'

import PropTypes from 'prop-types'
import TextInput from 'components/UI/TextInput'
import classNames from 'classnames'
import { connect } from 'react-redux'
import kebabCase from 'lodash/kebabCase'
import { searchAddress } from 'actions/AddressHelper'

export class AddressInput extends Component {
    constructor() {
        super()
        this.state = {
            cursor: -1,
            showDropdown: false,
        }
    }

    _currentValue = null

    componentWillReceiveProps(nextProps) {
        if (nextProps.items != this.props.items) {
            const cursor = this.findBestMatch(nextProps.items, this._currentValue)
            this.setState({ cursor })
        }
    }

    componentWillUnmount() {
        this.unmounted = true
    }

    findBestMatch(items, value) {
        const { findBestMatchIndex } = this.props
        return findBestMatchIndex ? findBestMatchIndex(items, value) : 0
    }

    select(index, autoNavigate, wasTabKey) {
        if (this.unmounted) return

        const selectedItem = this.props.items[index]
        if (selectedItem) {
            const fieldName = `${kebabCase(this.props.name)}`

            this.props.dispatch(
                searchAddress({
                    [fieldName]: this.props.view(selectedItem),
                })
            )

            this.props.onComplete(selectedItem, this.props.name, autoNavigate, wasTabKey)
        }

        this.setState({ showDropdown: false })
    }

    handleChange(newValue) {
        const { items } = this.props
        const cursor = this.findBestMatch(items, newValue)

        this.setState({ cursor, showDropdown: true })
        this.props.dispatch(searchAddress({ [`${kebabCase(this.props.name)}`]: newValue }))
        if (this.props.onChange) {
            this.props.onChange(newValue)
        }

        this._currentValue = newValue
    }

    handleKeyDown(e) {
        const { cursor } = this.state
        const { items } = this.props

        if (items.length === 0) {
            return
        }
        if (e.key === 'Tab' && !e.shiftKey) {
            this.select(cursor, false, true)
        }
        if (e.key === 'Enter') {
            this.select(cursor, true)
        }
        if (e.key === 'ArrowDown') {
            const nextPos = items[cursor + 1] ? cursor + 1 : 0
            this.setState({ cursor: nextPos, showDropdown: true })
        }
        if (e.key === 'ArrowUp') {
            const prevPos = items[cursor - 1] ? cursor - 1 : items.length - 1
            this.setState({ cursor: prevPos, showDropdown: true })
        }
    }

    preventSubmit(e) {
        e.key === 'Enter' && e.preventDefault()
    }

    getTimestamp() {
        return '' + Math.floor(moment().unix() / 15) // Changes every 15 sec
    }

    componentDidUpdate(prevProps, prevState) {
        const currentCursor = this.state.cursor
        const prevCursor = prevState.cursor
        if (currentCursor !== prevCursor) {
            this.setActiveElement(currentCursor, prevCursor)
        }
    }

    setActiveElement = (index, prevIndex) => {
        if (!this.dropdown || index === -1) return
        const element = this.dropdown.children[index]
        if (!element) return
        const position = element.offsetTop
        const height = element.offsetHeight
        const scrollEnd = this.dropdown.offsetHeight + this.dropdown.scrollTop

        if (position + height > scrollEnd || position < this.dropdown.scrollTop) {
            index > prevIndex
                ? (this.dropdown.scrollTop = position + height - this.dropdown.offsetHeight)
                : (this.dropdown.scrollTop = position)
        }
    }

    validate = (value) => {
        const { items, isActive } = this.props
        return !isActive || (items && this.findBestMatch(items, value) > -1)
    }

    render() {
        const { items, label, view, className, required, fillHelp, onFocus } = this.props
        const { showDropdown, cursor } = this.state
        const timeStamp = this.getTimestamp()
        const classes = classNames({
            'address-input': true,
            [`${className}`]: !!className,
        })
        const dropdownClasses = classNames({
            'address-input__dropdown': true,
            'address-input__dropdown--open': showDropdown,
        })

        return (
            <div
                className={classes}
                onKeyDown={(e) => {
                    this.handleKeyDown(e)
                }}
            >
                <TextInput
                    onChange={(e) => {
                        this.handleChange(e)
                    }}
                    onBlur={(value, event) => {
                        if (event.relatedTarget && event.relatedTarget.type === 'submit') {
                            this.select(this.state.cursor, true)
                        } else {
                            setTimeout(() => {
                                this.select(this.state.cursor, false)
                            }, 200)
                        }
                    }}
                    onKeyPress={this.preventSubmit}
                    validator={this.validate}
                    inputRef={this.props.inputRef}
                    value={this.props.value}
                    label={label}
                    labelPostfix={timeStamp} // To get unique id to avoid autocomplete/fill
                    onFocus={() => {
                        onFocus && onFocus()
                        this.setState({ showDropdown: true })
                    }}
                    autoComplete="off"
                    fillHelp={fillHelp}
                    required={required}
                >
                    {this.props.children}
                </TextInput>
                {showDropdown && (
                    <div className={dropdownClasses} ref={(r) => (this.dropdown = r)}>
                        {items &&
                            items.map((item, idx) => {
                                const classes = classNames({
                                    'address-input__item': true,
                                    'address-input__item--active': idx === cursor,
                                })
                                return (
                                    <div
                                        key={idx}
                                        className={classes}
                                        onClick={() => {
                                            this.setState({ cursor: idx })
                                            this.select(idx, true)
                                        }}
                                    >
                                        {view(item)}
                                    </div>
                                )
                            })}
                    </div>
                )}
            </div>
        )
    }
}

AddressInput.propTypes = {
    value: PropTypes.string,
    className: PropTypes.string,
    dispatch: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    isActive: PropTypes.bool,
    inputRef: PropTypes.func,
    items: PropTypes.array,
    view: PropTypes.func,
    findBestMatchIndex: PropTypes.func,
    name: PropTypes.string,
    label: PropTypes.string,
    searchValues: PropTypes.object,
    onComplete: PropTypes.func,
    fillHelp: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    required: PropTypes.bool,
}

const mapStateToProps = (state) => {
    return {
        searchValues: state.addressHelper.searchValues,
    }
}

export default connect(mapStateToProps)(AddressInput)
